import React from 'react';
import { ExtractedAddress } from '../../types';
import { getAddressTypeDisplayName, getAddressTypeColor } from '../../utils/addressAutoDetection';
import { extractPhoneNumbersFromMultipleTexts } from '../../utils/addressExtraction';
import logger from '../../utils/logger';

interface AddressSelectionUIProps {
  addresses: ExtractedAddress[];
  onAddressSelect: (address: ExtractedAddress) => void;
  onClose: () => void;
  title: string;
  actionButtonText: string;
  colorScheme: 'green' | 'blue';
}

/**
 * 住所選択UI共通コンポーネント
 */
export const AddressSelectionUI: React.FC<AddressSelectionUIProps> = ({
  addresses,
  onAddressSelect,
  onClose,
  title,
  actionButtonText,
  colorScheme,
}) => {
  const bgColorClass =
    colorScheme === 'green'
      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';

  const titleColorClass =
    colorScheme === 'green'
      ? 'text-green-800 dark:text-green-200'
      : 'text-blue-800 dark:text-blue-200';

  const buttonColorClass =
    colorScheme === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className={`p-4 border rounded-lg ${bgColorClass}`}>
      <h4 className={`font-medium mb-3 ${titleColorClass}`}>
        {title} ({addresses.length}件)
      </h4>
      <div className='space-y-3'>
        {addresses.map(address => (
          <div
            key={address.id}
            className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3'
          >
            <div className='flex items-start justify-between'>
              <div className='flex-1'>
                <p className='font-medium text-gray-900 dark:text-gray-100'>
                  {address.fullAddress}
                </p>
                {/* FL-67: 電話番号の自動抽出表示 */}
                {(() => {
                  try {
                    const phoneNumbers = extractPhoneNumbersFromMultipleTexts([address.rawText]);
                    return (
                      phoneNumbers.length > 0 && (
                        <p className='text-sm font-medium text-green-600 dark:text-green-400 mt-1'>
                          📞 {phoneNumbers[0].phoneNumber} (信頼度:{' '}
                          {Math.round(phoneNumbers[0].confidence * 100)}%)
                        </p>
                      )
                    );
                  } catch (error) {
                    logger.error('[AddressSelectionUI] 電話番号抽出エラー:', error);
                    return null;
                  }
                })()}
                <div className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                  <p>
                    OCR信頼度: {Math.round(address.confidence * 100)}% | 元テキスト:{' '}
                    {address.rawText}
                  </p>
                  {address.detectedType && (
                    <div className='mt-1 flex items-center'>
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs mr-2 ${getAddressTypeColor(address.detectedType as any)}`}
                      >
                        AI判定: {getAddressTypeDisplayName(address.detectedType as any)}
                      </span>
                      <span className='text-xs'>
                        判定信頼度: {Math.round((address.detectionConfidence || 0) * 100)}%
                      </span>
                    </div>
                  )}
                  {address.detectionReason && (
                    <p className='text-xs mt-1 text-gray-500'>{address.detectionReason}</p>
                  )}
                </div>
              </div>
              <button
                type='button'
                onClick={() => onAddressSelect(address)}
                className={`px-4 py-2 text-white text-sm rounded-lg transition-colors ml-3 whitespace-nowrap ${buttonColorClass}`}
              >
                {actionButtonText}
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        type='button'
        onClick={onClose}
        className='mt-3 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
      >
        選択を閉じる
      </button>
    </div>
  );
};
